import React, { useState, useEffect, useMemo } from "react";
import { NotFound } from "components";
import { Transition } from "@headlessui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "assets/img";
import { useNavigate } from "react-router-dom";
import dateTime from "utils/helpers/dateTime";
import { isEmpty, isPresent } from "utils/helpers/array";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  uncateringShipmentStatus,
  cateringShipmentStatus,
  groupedShipmentStatus,
} from "utils/helpers/shipment";
import { toJS } from "mobx";
import { formattedAddres } from "utils/helpers/string";
import classNames from "classnames";

const ExpandableRow = ({ mealsArray, locationId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Use first meal as representative for the row summary
  const meal = mealsArray[0];
  const service = meal?.attributes?.service;
  const orders = meal?.attributes?.orders ?? [];
  const currentTimeInTz = dayjs().tz(meal?.attributes?.tzinfo);
  const cutoffTime = dayjs(meal?.attributes?.cutoff).tz(
    meal?.attributes?.tzinfo,
    true
  );

  const notCancelledOrders = orders.filter(
    (order) => order?.attributes?.status !== "cancelled"
  );
  const inProgressOrders = orders.filter(
    (order) =>
      order?.attributes?.status !== "cancelled" &&
      order?.attributes?.status !== "completed"
  );

  const allOrdersCompleted =
    orders.length > 0 &&
    orders.every((order) => order?.attributes?.status === "completed");

  const shouldShowDropdown = mealsArray.length > 1;

  const totalQuantity = mealsArray.reduce((total, subMeal) => {
    const subMealOrders = subMeal?.attributes?.orders ?? [];

    const subMealQuantity = subMealOrders.reduce((sum, order) => {
      // Skip cancelled orders
      if (order?.status == "cancelled") return sum;

      const items = order?.order_items;
      if (!Array.isArray(items)) return sum;

      const orderTotal = items.reduce((itemSum, item) => {
        const quantity = parseInt(item?.quantity, 10);
        return itemSum + (isNaN(quantity) ? 0 : quantity);
      }, 0);

      return sum + orderTotal;
    }, 0);

    return total + subMealQuantity;
  }, 0);

  const groupTotalOrders = mealsArray.reduce((total, subMeal) => {
    const orders = subMeal?.attributes?.orders ?? [];
    const uncheckedoutOrders = subMeal?.attributes?.uncheckedout_orders ?? [];

    const validOrdersCount = Array.isArray(orders) ? orders.length : 0;

    const validUncheckedoutCount = Array.isArray(uncheckedoutOrders)
      ? uncheckedoutOrders.filter(
          (order) => order?.attributes?.status != "cancelled"
        ).length
      : 0;

    return total + validOrdersCount + validUncheckedoutCount;
  }, 0);

  return (
    <div className="w-full">
      {/* Header Row */}
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => shouldShowDropdown && setIsOpen(!isOpen)}
      >
        <div className="grid grid-cols-8 w-full bg-light-gray min-h-[68px] items-center px-[12px] mt-[12px] rounded-md">
          <div className="col-span-1">
            <span
              className={`font-inter-semibold capitalize text-[14px] ${
                service === "catering" ? "text-catering" : "text-group-order"
              }`}
            >
              {service === "odc" || service === "catering"
                ? "Catering"
                : service === "grouped"
                ? "Group Order"
                : service ?? "Not specified"}
            </span>
          </div>

          <div className="col-span-1">
            <p
              title={meal?.attributes?.name}
              className="font-inter-semibold text-[14px] text-dark-gray truncate"
            >
              {["catering", "odc"].includes(service)
                ? ""
                : service == "grouped" && mealsArray.length == 1
                ? meal?.attributes?.name
                : ""}
            </p>
          </div>

          <div className="col-span-1">
            <p className="font-inter-semibold pr-2 text-[14px] text-dark-gray break-all">
              {(!meal?.attributes?.under_review || service !== "uncatering") &&
                mealsArray.length == 1 && (
                  <>
                    {meal?.attributes?.address?.attributes?.nickname ?? "-"} (
                    {meal?.attributes?.address?.attributes?.destination_code})
                  </>
                )}
            </p>
          </div>

          <div className="col-span-1">
            <p className="font-inter-semibold text-[14px] text-dark-gray">
              {!meal?.attributes?.under_review && (
                <>
                  {(service === "grouped" ||
                    service === "catering" ||
                    service === "odc") &&
                    mealsArray.length == 1 &&
                    meal?.attributes?.restaurant_name}
                </>
              )}
            </p>
          </div>

          <div className="col-span-1">
            <span className="font-inter-semibold text-[14px] text-dark-gray">
              {service !== "grouped" ? totalQuantity : groupTotalOrders}
            </span>
          </div>

          <div className="col-span-1">
            {mealsArray.length == 1 ? (
              <span className="font-inter-semibold text-[14px] text-dark-gray">
                {!meal?.attributes?.published &&
                  service != "grouped" &&
                  "Restaurants to be picked soon"}

                {!meal?.attributes?.published &&
                  service == "grouped" &&
                  "Deadline to order: " +
                    dateTime.formatTimeWithFormat(
                      meal?.attributes?.cutoff,
                      "h:mm A",
                      meal?.attributes?.tzinfo
                    )}

                {meal?.attributes?.published &&
                  orders.length === 0 &&
                  currentTimeInTz.isBefore(cutoffTime) &&
                  "No orders placed"}

                {meal?.attributes?.published &&
                  notCancelledOrders.length === 0 &&
                  "No orders placed"}

                {meal?.attributes?.published &&
                  currentTimeInTz.isAfter(cutoffTime) &&
                  inProgressOrders.length > 0 &&
                  "In Progress"}

                {meal?.attributes?.published &&
                  currentTimeInTz.isBefore(cutoffTime) &&
                  inProgressOrders.length > 0 &&
                  "Order sent To restaurant"}

                {meal?.attributes?.published &&
                  allOrdersCompleted &&
                  "Complete"}
              </span>
            ) : null}
          </div>

          {mealsArray.length == 1 && (
            <div className="col-span-1">
              <div className="d-row items-center justify-center md:px-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (service == "grouped") {
                      navigate(
                        `/group-order/admin/orders/${meal?.attributes?.meal_series_id}/${locationId}`
                      );
                    } else if (["catering", "odc"].includes(service)) {
                      navigate(
                        `/meal-management/catering-order/${meal?.attributes?.orders[0]?.slug}/${locationId}`
                      );
                    }
                  }}
                  className="btn-outline w-[93px] font-inter-medium border-2 text-catering px-0"
                >
                  View order
                </button>
              </div>
            </div>
          )}

          <div className="col-span-1"></div>

          <div className="col-span-1">
            {shouldShowDropdown && (
              <div className="d-row items-center justify-center">
                <img
                  src={isOpen ? ChevronLeftIcon : ChevronRightIcon}
                  className="h-[20px] w-[20px] transform-left transform rotate-90"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expandable content for multiple meals */}
      {shouldShowDropdown && (
        <Transition
          show={isOpen}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          {mealsArray?.map((subMeal, index) => (
            <div
              key={index}
              className="grid grid-cols-8 w-full bg-white min-h-[68px] items-center px-[12px] mt-[12px] rounded-md"
            >
              <span></span>
              <span>
                <p className="font-inter-medium capitalize text-[14px] text-dark-gray pr-2 break-normal">
                  {service == "grouped" && subMeal?.attributes?.name}
                </p>
              </span>
              <span>
                <>
                  {subMeal?.attributes?.address?.attributes?.nickname ?? "-"} (
                  {subMeal?.attributes?.address?.attributes?.destination_code})
                </>
              </span>
              <p className="font-inter-medium capitalize text-[14px] text-dark-gray pr-2 break-normal">
                {subMeal?.attributes?.restaurant_name}
              </p>
              {service == "grouped" ? (
                <span className="font-inter-medium text-[14px] text-dark-gray">
                  {subMeal?.attributes?.status == "active"
                    ? subMeal?.attributes?.uncheckedout_orders?.filter(
                        (order) => order?.attributes?.status != "cancelled"
                      )?.length
                    : new Set(
                        subMeal?.attributes?.orders
                          ?.filter(
                            (order) => order?.attributes?.status !== "cancelled"
                          )
                          ?.map((order) => order?.attributes?.employee_id)
                      )?.size}
                </span>
              ) : (
                <span className="font-inter-medium text-[14px] text-dark-gray">
                  <span className="font-inter-medium text-[14px] text-dark-gray">
                    {subMeal?.attributes?.orders?.reduce((sum, order) => {
                      const items = order?.attributes?.order_items;

                      if (!Array.isArray(items)) return sum;

                      const orderTotal = items.reduce((itemSum, item) => {
                        const quantity = parseInt(
                          item?.attributes?.quantity,
                          10
                        );
                        return itemSum + (isNaN(quantity) ? 0 : quantity);
                      }, 0);

                      return sum + orderTotal;
                    }, 0)}
                  </span>
                </span>
              )}
              <span className="font-inter-medium text-[14px] text-dark-gray capitalize">
                {service === "grouped"
                  ? subMeal?.attributes?.shipments.length > 0
                    ? groupedShipmentStatus(
                        subMeal?.attributes?.shipments?.[0],
                        subMeal?.attributes?.tzinfo
                      )
                    : !subMeal?.attributes?.published &&
                      "Deadline to order: " +
                        dateTime.formatTimeWithFormat(
                          subMeal?.attributes?.cutoff,
                          "h:mm A",
                          subMeal?.attributes?.tzinfo
                        )
                  : cateringShipmentStatus(
                      subMeal?.attributes?.shipments?.[0],
                      subMeal?.attributes?.tzinfo
                    )}
              </span>

              <div className="d-row items-center justify-center md:px-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();

                    if (service == "grouped") {
                      navigate(
                        `/group-order/admin/orders/${subMeal?.attributes?.meal_series_id}/${locationId}`
                      );
                    } else if (["catering", "odc"].includes(service)) {
                      navigate(
                        `/meal-management/catering-order/${subMeal?.attributes?.orders[0]?.attributes?.slug}/${locationId}`
                      );
                    }
                  }}
                  className="btn-outline font-inter-medium text-catering border-2"
                >
                  View order
                </button>
              </div>
            </div>
          ))}
        </Transition>
      )}
    </div>
  );
};

const UncateringExpandableRow = ({ meal, locationId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const service = meal?.attributes?.service;

  const totalUsersOrdered = meal?.attributes?.shipments?.reduce(
    (sum, shipment) => sum + (shipment?.users_ordered_count || 0),
    0
  );

  const notCancelledOrders = meal?.attributes?.orders?.filter(
    (order) => order?.attributes?.status !== "cancelled"
  );

  const currentTimeInTz = dayjs().tz(meal?.attributes?.tzinfo);

  const cutoffTime = dayjs(meal?.attributes?.cutoff).tz(
    meal?.attributes?.tzinfo,
    true
  );

  const oneOrderInProgress = meal?.attributes?.orders?.filter(
    (order) =>
      order?.attributes?.status != "cancelled" &&
      order?.attributes?.status != "completed"
  );

  const allOrdersCompleted =
    isPresent(meal?.attributes?.orders) &&
    meal?.attributes?.orders?.every(
      (order) => order?.attributes?.status == "completed"
    )
      ? true
      : false;

  return (
    <div className="w-full">
      {/* Row Header */}
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="grid grid-cols-8 w-full bg-light-gray min-h-[68px] items-center px-[12px] mt-[12px] rounded-md">
          <div className="col-span-1">
            <span className="font-inter-semibold capitalize text-[14px] text-uncatering">
              {meal.attributes?.service ?? "Not specified"}
            </span>
          </div>

          <div className="col-span-1">
            <p
              title={meal?.attributes?.name}
              className="font-inter-semibold text-[14px] text-dark-gray truncate ..."
            >
              {meal?.attributes?.name}
            </p>
          </div>

          <div className="col-span-1">
            <p
              title={formattedAddres(meal?.attributes?.address?.attributes)}
              className="font-inter-semibold pr-2 text-[14px] break-normal text-dark-gray break-all"
            >
              {(!meal?.attributes?.under_review || service != "uncatering") && (
                <>
                  {meal?.attributes?.address?.attributes?.nickname ?? "-"} (
                  {meal?.attributes?.address?.attributes?.destination_code})
                </>
              )}
            </p>
          </div>

          <div className="col-span-1 overflow-scroll text-no-wrap">
            <p className="font-inter-semibold text-[14px] text-dark-gray break-normal">
              {!meal?.attributes?.under_review && (
                <>
                  {isPresent(meal?.attributes?.shipments) && !isOpen ? (
                    <div className="d-col">
                      <small className="text-[13px] font-inter-semibold text-dark-gray">
                        {meal?.attributes?.shipments[0]?.menu_display_name}
                      </small>

                      {meal?.attributes?.shipments?.length > 1 && (
                        <small className="font-inter-medium text-[13px] underline">
                          + {meal?.attributes?.shipments?.length} more
                        </small>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </>
              )}
            </p>
          </div>

          <div className="col-span-1">
            <span className="font-inter-semibold text-[14px] text-dark-gray">
              {!meal?.attributes?.under_review && totalUsersOrdered}
            </span>
          </div>

          <div className="col-span-1">
            {meal?.attributes?.under_review ? (
              <small className="font-inter-medium text-[12px] text-[#E39F55] pb-[4px]">
                Under review
              </small>
            ) : (
              <span className="font-inter-semibold text-[14px] text-dark-gray">
                {!meal?.attributes?.published &&
                  service != "grouped" &&
                  "Restaurants to be picked soon"}

                {meal?.attributes?.published &&
                  isEmpty(meal?.attributes?.orders) &&
                  currentTimeInTz.isBefore(cutoffTime) &&
                  "No orders placed"}

                {meal?.attributes?.published &&
                  isPresent(meal?.attributes?.orders) &&
                  isEmpty(notCancelledOrders) &&
                  "No orders placed"}

                {meal?.attributes?.published &&
                  isPresent(meal?.attributes?.orders) &&
                  currentTimeInTz.isAfter(cutoffTime) &&
                  isPresent(oneOrderInProgress) &&
                  "In Progress"}

                {meal?.attributes?.published &&
                  isPresent(meal?.attributes?.orders) &&
                  currentTimeInTz.isBefore(cutoffTime) &&
                  isPresent(oneOrderInProgress) &&
                  "Deadline to order: " +
                    dateTime.formatTimeWithFormat(
                      meal?.attributes?.cutoff,
                      "h:mm A",
                      meal?.attributes?.tzinfo
                    )}

                {meal?.attributes?.published &&
                  allOrdersCompleted &&
                  "Complete"}
              </span>
            )}
          </div>

          <div className="col-span-1">
            {service != "catering" &&
              service != "odc" &&
              (!meal?.attributes?.under_review || service != "uncatering") && (
                <div className="d-row items-center justify-center md:px-1">
                  <button
                    onClick={() => {
                      navigate(`/home/uncatering-meal/${meal?.id}`);
                    }}
                    className="btn-outline w-[93px] font-inter-medium border-2 text-catering px-0"
                  >
                    View order
                  </button>
                </div>
              )}
          </div>

          <div className="col-span-1">
            {(!meal?.attributes?.under_review || service != "uncatering") &&
              meal?.attributes?.shipments?.length > 0 && (
                <div className="d-row items-center justify-center">
                  <img
                    src={isOpen ? ChevronLeftIcon : ChevronRightIcon}
                    className="h-[20px] w-[20px] transform-left transform rotate-90"
                  />
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <Transition
        show={isOpen}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        {meal?.attributes?.shipments?.map((shipment, ind, arr) => {
          return (
            <div className="grid grid-cols-8 w-full bg-white min-h-[68px] items-center px-[12px] mt-[12px] rounded-md">
              <span></span>
              <span></span>
              <span></span>
              <p
                title={shipment?.menu_name}
                className="font-inter-medium capitalize text-[14px] text-dark-gray pr-2 break-words"
              >
                {shipment?.menu_display_name}
              </p>
              <span className={`font-inter-medium text-[14px] text-dark-gray`}>
                {shipment?.users_ordered_count}
              </span>

              <span className="font-inter-medium text-[14px] text-dark-gray capitalize">
                {uncateringShipmentStatus(
                  shipment,
                  meal?.attributes?.tzinfo,
                  meal?.attributes?.target_time
                )}
              </span>

              <div className="d-row items-center justify-center md:px-1"></div>

              <div></div>
            </div>
          );
        })}
      </Transition>
    </div>
  );
};

function ActiveMeals({
  data,
  locationId,
  currentDate,
  availableDates,
  onDateChange,
}) {
  const [screenSize, setScreenSize] = useState("md");
  const [expandData, setExpandData] = useState(false);
  const navigate = useNavigate();

  const getLimitedData = (limit = 5) => {
    let flatList = [];

    data?.uncatering?.forEach((meal) => {
      flatList.push({ serviceType: "uncatering", meal });
    });

    ["grouped", "catering"].forEach((type) => {
      Object.entries(data?.[type] || {}).forEach(([timestamp, meals]) => {
        meals.forEach((meal) => {
          flatList.push({ serviceType: type, timestamp, meal });
        });
      });
    });

    const limitedList = flatList.slice(0, limit);

    // Rebuild the structure
    const limitedData = {
      uncatering: [],
      grouped: {},
      catering: {},
    };

    limitedList.forEach(({ serviceType, timestamp, meal }) => {
      if (serviceType === "uncatering") {
        limitedData.uncatering.push(meal);
      } else {
        if (!limitedData[serviceType][timestamp]) {
          limitedData[serviceType][timestamp] = [];
        }
        limitedData[serviceType][timestamp].push(meal);
      }
    });

    return limitedData;
  };

  const totalMealsCount =
    (data?.uncatering?.length || 0) +
    Object.values(data?.grouped || {}).reduce(
      (acc, meals) => acc + meals.length,
      0
    ) +
    Object.values(data?.catering || {}).reduce(
      (acc, meals) => acc + meals.length,
      0
    );

  const visibleData =
    !expandData && totalMealsCount > 5 ? getLimitedData(5) : data;

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });

    const handleResize = () => {
      if (window.innerWidth < 768) {
        setScreenSize("sm");
      } else if (window.innerWidth < 1024) {
        setScreenSize("md");
      } else {
        setScreenSize("lg");
      }
    };

    handleResize(); // Initial call to set the initial screen size

    window.addEventListener("resize", handleResize); // Listen for window resize events

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const nextAvailableDate = useMemo(() => {
    return availableDates
      .map((d) => dayjs(d))
      .find((d) => d.isAfter(currentDate, "day"));
  }, [availableDates, currentDate]);

  const hasMealData =
    (Array.isArray(data?.uncatering) && data.uncatering.length > 0) ||
    ["grouped", "catering"].some((service) =>
      Object.values(data?.[service] || {}).some(
        (mealsArray) => Array.isArray(mealsArray) && mealsArray.length > 0
      )
    );

  if (hasMealData) {
    return (
      <div className="flex-col w-full items-center flex">
        <div className="grid grid-cols-8 w-full bg-light-gray h-[46px] items-center px-[12px] rounded-md mt-[24px]">
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Type
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Name
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Destination
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Restaurant
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Users Ordered
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray">
            Status
          </span>
          <span className="font-inter-bold text-[14px] text-dark-gray"></span>
          <span className="font-inter-bold text-[14px] text-dark-gray"></span>
        </div>

        <div className=" mx-auto w-full">
          <div className="w-full">
            {Array.isArray(visibleData?.uncatering) &&
              visibleData.uncatering?.map((meal, index) => (
                <UncateringExpandableRow
                  key={`uncatering-${index}`}
                  meal={meal}
                  locationId={locationId}
                />
              ))}
          </div>

          <div className="w-full">
            {Object.entries(visibleData)?.map(
              ([serviceType, timestampGroups]) =>
                ["catering", "grouped"].includes(serviceType)
                  ? Object.entries(timestampGroups || {})?.map(
                      ([timestamp, mealsArray]) => (
                        <ExpandableRow
                          key={`${serviceType}-${timestamp}`}
                          mealsArray={mealsArray}
                          locationId={locationId}
                        />
                      )
                    )
                  : null
            )}
          </div>

          {totalMealsCount > 5 && (
            <div className="d-row justify-center w-full">
              <button
                onClick={() => {
                  setExpandData(!expandData);

                  window.scroll({ top: 0, left: 0, behavior: "smooth" });
                }}
                className="text-catering d-row items-center font-inter-medium text-[16px] mt-[18px]"
              >
                {expandData ? "Show Less Orders" : "Show All Orders"}

                <FontAwesomeIcon
                  icon={expandData ? faChevronLeft : faChevronRight}
                  style={{
                    color: "#575BE8",
                  }}
                  className="transform-left ml-2 transform rotate-90"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="d-col items-center">
      <NotFound data={data} message="No meals on this day" />

      <button
        onClick={() => {
          onDateChange(nextAvailableDate);
        }}
        disabled={!nextAvailableDate}
        className={classNames({
          "mb-[12px] text-catering font-inter-medium text-[14px] hover:text-blue-500": true,
          "opacity-70 cursor-not-allowed": !nextAvailableDate,
        })}
      >
        Skip to Next Meal
      </button>

      <button
        onClick={() => {
          navigate("/our-services");
        }}
        className="btn-blue-accent"
      >
        Learn about our services
      </button>
    </div>
  );
}

export default ActiveMeals;
